// Shared shape and defaults for the About page content, stored in the
// `aboutMe` row of the `settings` table and edited from the admin dashboard.

export type ExperienceEntry = { role: string; company: string; period: string; description: string };
export type EducationEntry = { school: string; degree: string };
export type SkillGroup = { category: string; items: string };

export type AboutContent = {
  image: string;
  name: string;
  content: string;
  experience: ExperienceEntry[];
  education: EducationEntry[];
  skills: SkillGroup[];
  courses: string[];
  recognitions: string;
};

export const DEFAULT_ABOUT: AboutContent = {
  image: '',
  name: 'Elmunzer Abdelkarim',
  content: 'Senior UX/UI Designer based in Riyadh, Saudi Arabia. I specialize in architecting scalable design systems and standardizing component libraries to streamline development handoffs while modernizing legacy interfaces and transforming complex user journeys into intuitive, accessible digital experiences.',
  experience: [
    {
      role: 'Senior UX/UI Designer',
      company: 'Datalink Kuwait',
      period: 'Aug 2025 - Present',
      description: 'Led the end-to-end design and front-end development of high-impact web and mobile solutions—including corporate sites, e-commerce platforms, and service applications—while collaborating with cross-functional technical teams to drive brand credibility and user engagement.',
    },
    {
      role: 'Senior UX/UI Designer',
      company: 'Menzies Missions',
      period: 'Sep 2025 - Dec 2025',
      description: 'Architected scalable design systems and standardized component libraries to streamline development handoffs while modernizing legacy interfaces and transforming complex user journeys into intuitive, accessible digital experiences.',
    },
    {
      role: 'UX/UI Designer',
      company: 'Siciliamia',
      period: 'July 2025 - Nov 2025',
      description: 'Directed the UI/UX redesign of hospitality and tourism platforms by establishing scalable design systems and reusable component libraries while delivering fully responsive, custom-coded marketing assets to ensure brand consistency across all digital touchpoints.',
    },
    {
      role: 'Founding UX/UI Designer',
      company: 'Adawat Learning Platform',
      period: 'Feb 2025 - Aug 2025',
      description: 'Led the award-winning end-to-end design and strategy for a groundbreaking Arabic language platform, from foundational user research and discovery to architecting an intuitive UI/UX system validated through rigorous usability testing.',
    },
    {
      role: 'UX/UI Design Intern',
      company: 'Ninja',
      period: 'Sep 2024 - Jan 2025',
      description: 'Redesigned the supermarket experience for a leading shopping app with 8 million+ active users, leveraging localized user research and usability testing to streamline Saudi-specific user flows and significantly reduce cart abandonment rates.',
    },
    {
      role: 'UX/UI Designer',
      company: 'Bank of Khartoum',
      period: 'Feb 2024 - Aug 2024',
      description: 'Redesigned the mobile banking experience for 15 million+ users, slashing task completion time by 70% and login time by 90% through the integration of biometric security and a revamped, user-centered account recovery flow.',
    },
    {
      role: 'Web Development Intern',
      company: 'Kreativestorm',
      period: 'Jan 2024 - Feb 2024',
      description: 'Engineered responsive, client-specific web applications within a collaborative developer environment, utilizing HTML/CSS/JS and GitHub to establish coding standards and ensure seamless cross-browser performance.',
    },
  ],
  education: [
    { school: 'Khartoum University', degree: 'Bcs in Computer Science & Mathematics' },
  ],
  skills: [
    { category: 'Design', items: 'Figma, Adobe Creative Suite, Sketch, Miro, User Research, A/B Testing, Design Systems, Storytelling' },
    { category: 'Technical', items: 'HTML/CSS, JavaScript, React' },
  ],
  courses: [
    'ShiftNudge Design Course',
    'Cradis UX/UI Design Bootcamp',
    'Google UX Design Professional Certificate',
  ],
  recognitions: 'Selected as a top 12 finalist from a pool of 180+ companies in the King Salman Global Academy for Arabic Language Business Accelerator.',
};

// Merge a saved `aboutMe` row over the defaults so older rows (which only
// held `content` and `image`) keep showing the original section content.
export const mergeAboutContent = (data: Partial<AboutContent> | null | undefined): AboutContent => {
  const saved = data || {};
  return {
    image: saved.image ?? DEFAULT_ABOUT.image,
    name: saved.name || DEFAULT_ABOUT.name,
    content: saved.content || DEFAULT_ABOUT.content,
    experience: Array.isArray(saved.experience) ? saved.experience : DEFAULT_ABOUT.experience,
    education: Array.isArray(saved.education) ? saved.education : DEFAULT_ABOUT.education,
    skills: Array.isArray(saved.skills) ? saved.skills : DEFAULT_ABOUT.skills,
    courses: Array.isArray(saved.courses) ? saved.courses : DEFAULT_ABOUT.courses,
    recognitions: saved.recognitions ?? DEFAULT_ABOUT.recognitions,
  };
};
